/**
 * หน้าฝึก — ใช้ร่วมกันทุก deck ทุกโหมด
 *
 * หลักที่ยึด (DESIGN.md §ความเร็ว):
 *   ตอบถูก → ไปข้อถัดไป *ทันที* ไม่มีหน่วง
 *   ตอบผิด → เฉลยค้างไว้จนกว่าจะกด (นั่นคือจังหวะที่ได้เรียนจริง)
 *   พิมพ์ครบแล้วตัดสินเลย ไม่ต้องกด Enter
 */
import type { Card, CardId, DeckId, DrillStat, Progress, Rating } from '@/core/types'
import { CardState } from '@/core/types'
import type { Deck, ModeId } from '@/decks/types'
import { isLessonMode, isPickerMode, isTypingMode, MODE_LABEL } from '@/decks/types'
import { DECKS, filterCards } from '@/decks'
import { judge, bestAnswer, maxAnswerLength } from '@/core/grade'
import {
  buildLessonQueue,
  buildSrsQueue,
  buildAutoQueue,
  pickDrill,
  updateDrill,
  drillMastered,
  MAX_ANSWER_MS,
} from '@/core/queue'
import { newProgress, previewIntervals, schedule, isLeech } from '@/core/srs'
import { bushuDeck } from '@/decks/bushu'
import { kanjiDeck } from '@/decks/kanji'
import {
  drillWeakness,
  medianMs,
  medianModeMs,
  medianProgressMs,
  modeWeakness,
  rankWeak,
  REASON_LABEL,
  srsWeakness,
  statsByMode,
  type ModeStat,
  type Weakness,
} from '@/core/weakness'
import {
  addLog,
  getLogSince,
  getDrillAll,
  getOverride,
  getProgressByDeck,
  putDrill,
  putOverride,
  putProgress,
  type CardOverride,
} from '@/core/db'
import { hasJapaneseVoice, playKana, prefetch, speakText } from '@/audio/play'
import { createKeypad, onPointerTypeChange, shouldUseKeypad, type Keypad } from './keypad'
import { pickFont, fontById } from './fonts'
import { settings, getPicked, saveSettings } from './settings'
import { createStrokePlayer } from './stroke'
import { prefetchStrokes } from '@/core/strokes'
import { kunWords, type KunWord } from '@/core/reading'

interface Els {
  square: HTMLElement
  glyph: HTMLElement
  peek: HTMLElement
  note: HTMLElement
  audioBtn: HTMLButtonElement
  strokeBtn: HTMLButtonElement
  answer: HTMLElement
  input: HTMLInputElement
  hint: HTMLElement
  tiles: HTMLElement
  reveal: HTMLElement
  keypadHost: HTMLElement
  counter: HTMLElement
}

const CHOICE_COUNT = 4

/** ย้อนดูสถิติรายโหมดกี่วัน — อยากรู้ว่าตอนนี้แน่นหรือยัง ไม่ใช่ประวัติทั้งชีวิต */
const MODE_STAT_DAYS = 90

export class Session {
  private deck!: Deck
  private mode!: ModeId
  private pool: Card[] = []
  /** ใบที่เลือกไว้ทั้งหมดในรอบนี้ — ก่อนกรองด้วยคิว/เก็บตก */
  private selected: Card[] = []
  /**
   * โหมดที่ระบบเลือกใบให้เอง — null = ผู้ใช้เลือกโหมดถามเอง
   *
   * แยกจาก this.mode เพราะเป็นคนละคำถาม: picker ตอบว่า "เอาใบไหนมา"
   * ส่วน mode ตอบว่า "ถามยังไง" · ใน auto ระบบตั้ง mode ให้ทีละใบ
   */
  private picker: 'auto' | null = null
  private queue: Card[] = []
  private current: Card | null = null
  private answered = false
  /** เปิดดูเฉลยก่อนตอบ — ตอบถูกหลังแอบดูไม่นับว่ารู้ */
  private peeked = false
  /** โหมดเก็บตกกรอง pool อยู่จริงไหม — เปิดโหมดไว้แต่ไม่มีตัวอ่อนก็เท่ากับไม่ได้กรอง */
  private weakActive = false
  /** จำนวนใบตอนเริ่มรอบเรียน — ใช้คิดว่าเรียนไปแล้วกี่ใบ */
  private lessonBatch = 0
  /**
   * โหมดทดสอบก่อนเรียน — สำหรับคนที่รู้อยู่แล้วบางส่วน
   *
   * ตอบถูก = บันทึกตามปกติ ใบนั้นผ่านไปเลยไม่ต้องเรียน
   * ตอบผิด = ไม่บันทึกอะไร ใบนั้นจึงยังเป็น "ยังไม่เคยเห็น" และรออยู่ในคิวเรียน
   *
   * ไม่มีการประกาศว่ารู้ ทุกใบผ่านการพิสูจน์ — ตรงกับที่ทั้งแอปยึดไว้
   */
  private probing = false
  /** ใบที่ทดสอบไปแล้วในรอบนี้ ทดสอบใบละครั้งเท่านั้น */
  private probed = new Set<CardId>()
  /** keyword/mnemonic ที่แต่งเองของใบปัจจุบัน */
  private override: CardOverride | undefined
  private shownAt = 0
  /**
   * เวลาที่ไม่ได้อยู่หน้าจอ สะสมของใบปัจจุบัน
   *
   * เวลาตอบเป็นสัญญาณว่าจำได้อัตโนมัติหรือยัง — สลับแท็บไปกินข้าวแล้วกลับมาตอบ
   * จะบันทึกว่าใช้เวลาห้านาที ใบนั้นก็ถูกตีตราว่ายังไม่แน่นทั้งที่จำได้แม่น
   */
  private awayMs = 0
  private awaySince = 0
  private lastId: CardId | undefined

  private progress = new Map<CardId, Progress>()
  private drill = new Map<CardId, DrillStat>()
  /** สถิติแยกตามโหมดถาม — key `cardId|mode` (ดู weakness.statsByMode) */
  private modeStats = new Map<string, ModeStat>()

  private keypad: Keypad
  /** สร้างก่อน els เพราะ build() เอา overlay ไปวางในช่องโจทย์ */
  private stroke = createStrokePlayer()
  /** ใบนี้มีลำดับขีดไหม — jinmeiyō รูปเก่าบางตัว KanjiVG ไม่ได้ทำไว้ */
  private hasStroke = false
  /**
   * อยากเห็นลำดับขีดอยู่ไหม — ค้างข้ามใบ
   *
   * เปิดดูใบหนึ่งแล้วมักอยากดูใบถัดไปด้วย · ถ้าให้กดใหม่ทุกใบก็เท่ากับบังคับกด
   * 200 ครั้งต่อรอบ · เสียงก็ทำแบบนี้อยู่แล้ว
   */
  private strokeWanted = false
  private els: Els

  /** นับเฉพาะ session นี้ ไม่บันทึกลง DB */
  private stats = { total: 0, right: 0, streak: 0, best: 0 }

  constructor(
    private root: HTMLElement,
    private onProgressChange: () => void,
    private onRequestMode: (mode: ModeId) => void,
    private onRequestDeck: (deck: DeckId) => void,
  ) {
    this.els = this.build()
    this.keypad = createKeypad(
      (text) => this.appendInput(text),
      () => this.backspace(),
    )
    this.els.keypadHost.append(this.keypad.el)
    this.bindKeys()
    this.watchVisibility()
  }

  // ---------- โครง DOM ----------

  private build(): Els {
    const stage = document.createElement('section')
    stage.className = 'stage w'
    stage.innerHTML = `
      <div class="square" id="sq" role="button" tabindex="0" title="คลิกเพื่อดูเฉลย (/)">
        <div class="glyph" id="glyph"></div>
        <div class="peek" id="peek"></div>
        <div class="square-note" id="note"></div>
        <div class="square-icons">
          <button class="square-ico" id="audio" type="button"
            title="ฟังเสียง (F)" aria-label="ฟังเสียง">🔊</button>
          <button class="square-ico" id="stroke" type="button"
            title="ลำดับขีด (W)" aria-label="ดูลำดับขีด">✎</button>
        </div>
        <svg class="mark" viewBox="0 0 100 100" aria-hidden="true">
          <circle class="m-o" cx="50" cy="50" r="38"></circle>
          <path class="m-x1" d="M25 25 L75 75"></path>
          <path class="m-x2" d="M75 25 L25 75"></path>
        </svg>
      </div>
      <div class="answer" id="answer">
        <input class="answer-input" id="input" type="text"
          autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false"
          inputmode="latin" aria-label="พิมพ์คำอ่าน" />
        <div class="answer-hint" id="answerHint"></div>
      </div>`

    const keypadHost = document.createElement('div')
    keypadHost.className = 'w'

    const tiles = document.createElement('div')
    tiles.className = 'tiles w'

    const reveal = document.createElement('section')
    reveal.className = 'reveal w'
    reveal.hidden = true

    const counter = document.createElement('span')

    const square = stage.querySelector<HTMLElement>('#sq')
    if (!square) throw new Error('missing #sq')
    square.append(this.stroke.overlay)

    this.root.append(stage, this.stroke.bar, keypadHost, tiles, reveal)

    const q = <T extends HTMLElement>(id: string): T => {
      const el = stage.querySelector<T>(`#${id}`)
      if (!el) throw new Error(`missing #${id}`)
      return el
    }

    return {
      square: q('sq'),
      glyph: q('glyph'),
      peek: q('peek'),
      note: q('note'),
      audioBtn: q<HTMLButtonElement>('audio'),
      strokeBtn: q<HTMLButtonElement>('stroke'),
      answer: q('answer'),
      input: q<HTMLInputElement>('input'),
      hint: q('answerHint'),
      tiles,
      reveal,
      keypadHost,
      counter,
    }
  }

  // ---------- เริ่ม session ----------

  async start(deck: Deck, mode: ModeId): Promise<void> {
    this.deck = deck
    /*
     * auto ไม่ใช่วิธีถาม — เก็บไว้แยกแล้วให้ this.mode เป็นโหมดถามจริงของใบที่
     * กำลังแสดงอยู่ · โค้ดส่วนที่เหลือทั้งไฟล์จึงไม่ต้องรู้จักมันเลย
     */
    this.picker = mode === 'auto' ? 'auto' : null
    this.mode = this.picker ? this.askMode(deck) : mode
    const selected = filterCards(deck, getPicked(deck.id))
    this.selected = selected

    /*
     * สถิติรายโหมดมาจาก ReviewLog ที่บันทึก mode ไว้ทุกครั้งอยู่แล้ว — ไม่ต้องมี
     * store ใหม่และไม่กระทบการซิงก์ · เอาแค่ 90 วันล่าสุดพอ เพราะสิ่งที่อยากรู้คือ
     * "ตอนนี้แน่นหรือยัง" ไม่ใช่ประวัติทั้งชีวิต
     */
    const logs = await getLogSince(Date.now() - MODE_STAT_DAYS * 86_400_000)
    this.modeStats = statsByMode(logs.filter((l) => l.deck === deck.id))

    if (deck.scheduling === 'srs') {
      const rows = await getProgressByDeck(deck.id)
      this.progress = new Map(rows.map((p) => [p.cardId, p]))
    } else {
      const rows = await getDrillAll()
      this.drill = new Map(rows.map((s) => [s.cardId, s]))
    }

    if (this.picker === 'auto') {
      this.probing = false
      const weak = this.weakScores(selected)
      // เปิดโหมดเก็บตกไว้ = ตัดใบที่แน่นแล้วออกทั้งหมด แม้จะถึงกำหนดทวนก็ตาม
      this.weakActive = settings.weakOnly && weak.size > 0
      const scoped = this.weakActive ? selected.filter((c) => weak.has(c.id)) : selected

      const seen = scoped.filter((c) => !this.isUnseen(c))
      const due =
        deck.scheduling === 'srs' ? buildSrsQueue(seen, this.progress) : this.dueDrill(seen)
      /*
       * ใบที่ถึงกำหนดพร้อมกันไม่ได้ด่วนเท่ากัน — ตัวที่ผิดซ้ำหรือยังต้องนึกนาน
       * ควรได้เจอตอนยังมีแรง ไม่ใช่ตอนท้ายคิว · ไม่มีคะแนนความอ่อนก็คงลำดับ due เดิม
       */
      due.sort((a, b) => (weak.get(b.id)?.score ?? 0) - (weak.get(a.id)?.score ?? 0))

      // เก็บตกอยู่ = ไม่ควรมีใบใหม่มาแทรก จะได้จบสิ่งที่ค้างก่อน
      const fresh = this.weakActive
        ? []
        : buildLessonQueue(selected, (c) => this.isUnseen(c), settings.newPerDay)
      this.queue = buildAutoQueue(due, fresh)
      /*
       * สำเนา ไม่ใช่ตัวเดียวกัน — pickNext() ทำ shift() กับ queue ทุกใบ ถ้าชี้ array
       * เดียวกัน pool ก็หดตามไปด้วย แล้วทุกอย่างที่วัดจาก pool (แถบ "คิววันนี้ N ใบ",
       * ตัวนับจำได้แล้ว, ข้อความตอนจบรอบ) ก็นับถอยหลังแทนที่จะบอกขนาดของรอบ
       */
      this.pool = [...this.queue]
      this.lessonBatch = fresh.length
    } else if (isLessonMode(mode)) {
      this.probing = false
      this.weakActive = false
      this.pool = buildLessonQueue(selected, (c) => this.isUnseen(c), settings.newPerDay)
      this.lessonBatch = this.pool.length
      this.queue = [...this.pool]
    } else if (this.probing) {
      // ทดสอบเฉพาะใบที่ยังไม่เคยเห็น และยังไม่ได้ทดสอบในรอบนี้
      this.weakActive = false
      this.pool = selected.filter((c) => this.isUnseen(c) && !this.probed.has(c.id))
      this.queue = [...this.pool]
    } else {
      // โหมดเก็บตก: เอาเฉพาะตัวที่ยังไม่แน่น · ไม่มีเลยก็ถอยกลับไปชุดปกติ
      const weak = this.weakOf(selected)
      this.weakActive = settings.weakOnly && weak.length > 0
      const scoped = this.weakActive
        ? selected.filter((c) => weak.some((w) => w.cardId === c.id))
        : selected

      // ใบที่ยังไม่เรียนไม่เข้าโหมดฝึก — ต้องผ่านโหมดเรียนก่อน ทั้ง SRS และ drill
      this.pool = scoped.filter((c) => !this.isUnseen(c))
      if (deck.scheduling === 'srs') this.queue = buildSrsQueue(this.pool, this.progress)
    }

    if (this.pool.some((c) => this.audioKey(c))) {
      prefetch(this.pool.map((c) => this.audioKey(c)).filter((s): s is string => !!s))
    }
    /*
     * เอาเฉพาะใบต้นคิว — pool ของ deck คันจิคือ 2,974 ใบกระจายอยู่ 12 ไฟล์ รวม 2.8 MB
     * ส่งทั้ง pool ไปเท่ากับสั่งโหลดทุกไฟล์ทันทีที่กดเข้า deck ทั้งที่รอบนั้นจะเจอไม่กี่ใบ
     * ที่เหลือ next() ตามเก็บให้ทีละนิดระหว่างเล่น
     */
    prefetchStrokes((this.queue.length > 0 ? this.queue : this.pool).slice(0, 40))

    // ครั้งแรกที่เห็นตัวหนึ่ง ควรได้เห็นวิธีเขียนพร้อมกัน เหมือนที่ได้ยินเสียงเลย
    // โหมดอื่นเริ่มจากปิด แล้วแต่จะกดเปิด — เปิดแล้วค้างจนกว่าจะกดปิดหรือเปลี่ยนโหมด
    this.strokeWanted = isLessonMode(mode) && settings.autoStroke

    this.stats = { total: 0, right: 0, streak: 0, best: 0 }
    this.next()
  }

  /** เวลาที่ใช้ตอบใบนี้จริง ๆ — หักช่วงที่ไม่ได้อยู่หน้าจอออกแล้ว */
  private elapsedMs(): number {
    const away = this.awayMs + (this.awaySince > 0 ? performance.now() - this.awaySince : 0)
    return Math.max(0, Math.round(performance.now() - this.shownAt - away))
  }

  /**
   * เริ่ม/หยุดนับตามการสลับแท็บ
   *
   * ไม่ต้องมีปุ่มหยุดชั่วคราว เพราะไม่มีอะไรวิ่งอยู่นอกจากนาฬิกาตัวนี้ — ทุกใบ
   * บันทึกทันทีที่ตอบ ปิดแอปกลางคันแล้วเปิดใหม่ก็ต่อได้เลย
   */
  private watchVisibility(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        if (this.awaySince === 0) this.awaySince = performance.now()
      } else if (this.awaySince > 0) {
        this.awayMs += performance.now() - this.awaySince
        this.awaySince = 0
      }
    })
  }

  /**
   * โหมดถามที่ใช้กับใบที่เรียนไปแล้ว
   *
   * เดิมคืนโหมดแรกของ deck เสมอ — ฝึกอัตโนมัติทั้งรอบจึงถามท่าเดียวตลอด
   * kana ได้แต่ "ตัวอักษร → เสียง" ส่วน "ฟัง → ตัวอักษร" กับ "เสียง → ตัวอักษร"
   * ไม่เคยโผล่เลยถ้าไม่ไปกดเลือกเอง ซึ่งค้านกับที่โหมดนี้บอกว่าจัดให้ครบ
   *
   * เลือกจากโหมดที่ใบนี้อ่อนที่สุดแทน — โหมดที่ยังไม่เคยถามมาก่อนมาก่อนใคร
   * เพราะ "ยังไม่มีหลักฐานว่าทำได้" ต่างจาก "เคยทำได้แล้ว" · เสมอกันก็สุ่ม
   * ไม่งั้นลำดับใน deck.modes กลายเป็นลำดับความสำคัญโดยไม่ได้ตั้งใจ
   */
  private askMode(deck: Deck, card?: Card): ModeId {
    const list = deck.modes.filter((m) => !isLessonMode(m) && !isPickerMode(m))
    const first = list[0] ?? 'recall'
    if (!card || list.length <= 1) return first

    // ไม่มีเสียงให้ฟังก็ถามด้วยเสียงไม่ได้ · breakdown กันตัวเองอยู่แล้วใน render
    const usable = list.filter((m) => m !== 'audio-to-glyph' || this.canSpeak(card))
    if (usable.length <= 1) return usable[0] ?? first

    let top: ModeId[] = []
    let best = -1
    for (const m of usable) {
      const stat = this.modeStats.get(`${card.id}|${m}`)
      const score = stat
        ? (modeWeakness(card, stat, this.modeRefMs(m))?.score ?? 0)
        : Number.POSITIVE_INFINITY
      if (score > best) {
        best = score
        top = [m]
      } else if (score === best) {
        top.push(m)
      }
    }
    return top[Math.floor(Math.random() * top.length)] ?? first
  }

  /** เวลาตอบกลางของโหมดหนึ่ง — เกณฑ์ "ช้า" ต้องเทียบกันเองในโหมดเดียวกัน */
  private modeRefMs(mode: ModeId): number {
    const mine: ModeStat[] = []
    for (const [key, stat] of this.modeStats) if (key.endsWith(`|${mode}`)) mine.push(stat)
    return medianModeMs(mine)
  }

  /**
   * ใบที่ควรทวนของ deck แบบ drill
   *
   * drill ไม่มีวันครบกำหนดเหมือน SRS — ใช้คะแนนความอ่อนแทน แล้วสลับลำดับ
   * เพื่อไม่ให้เจอเรียงเดิมทุกวัน
   */
  private dueDrill(seen: Card[]): Card[] {
    const weak = new Set(this.weakOf(seen).map((w) => w.cardId))
    const soft = seen.filter((c) => weak.has(c.id))
    const rest = seen.filter((c) => !weak.has(c.id))
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[rest[i], rest[j]] = [rest[j] as Card, rest[i] as Card]
    }
    return [...soft, ...rest]
  }

  // ---------- วนข้อ ----------

  /**
   * ยังไม่เคยเห็นใบนี้เลยไหม
   * SRS ดูจาก state · drill ไม่มี state จึงดูว่าเคยถูกถามหรือยัง
   */
  isUnseen(card: Card): boolean {
    if (this.deck.scheduling === 'drill') return (this.drill.get(card.id)?.seen ?? 0) === 0
    return (this.progress.get(card.id)?.state ?? CardState.New) === CardState.New
  }

  /** ชิ้นส่วนที่ประกอบเป็นคันจิตัวนี้ (จาก KRADFILE) */
  private partsOf(card: Card): string[] {
    const v = card.extra?.['parts']
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  }

  private next(): void {
    this.answered = false
    this.peeked = false
    this.els.square.classList.remove('is-correct', 'is-wrong', 'is-peek')
    this.els.peek.textContent = ''
    this.els.reveal.hidden = true
    this.els.reveal.replaceChildren()
    this.els.hint.textContent = ''
    this.keypad.reset()

    // ซ่อนก่อนหาใบถัดไป — จอว่างก็ต้องสะอาด ไม่ใช่ค้างภาพของใบก่อนไว้
    this.stroke.hide()
    this.hasStroke = false
    this.els.strokeBtn.hidden = true
    this.stroke.arm(false)

    const card = this.pickNext()
    if (!card) {
      this.showEmpty()
      return
    }
    /*
     * ในโหมดที่ระบบเลือกใบให้ ใบที่ยังไม่เคยเห็นต้องได้เรียนก่อน ไม่ใช่โผล่มาให้เดา
     * ตั้ง this.mode ตรงนี้ทีเดียว โค้ด render ทั้งหมดข้างล่างจึงทำงานเหมือนเดิม
     */
    if (this.picker) this.mode = this.isUnseen(card) ? 'lesson' : this.askMode(this.deck, card)

    this.current = card
    this.lastId = card.id
    this.shownAt = performance.now()
    this.awayMs = 0
    this.awaySince = document.visibilityState === 'hidden' ? performance.now() : 0
    // โหลดเรื่องที่แต่งไว้แบบไม่บล็อก — เฉลยจะยังไม่ถูกเปิดในไม่กี่ ms นี้อยู่แล้ว
    this.override = undefined
    void getOverride(card.id).then((o) => {
      if (this.current?.id === card.id) this.override = o
    })

    void this.loadStroke(card)
    // ไฟล์ที่โหลดแล้วไม่ถูกขอซ้ำ — เรียกทุกใบได้โดยไม่เสียอะไร
    prefetchStrokes(this.queue.slice(0, 8))

    this.renderPrompt(card)
    if (isLessonMode(this.mode)) this.renderLesson(card)
    else if (isTypingMode(this.mode)) this.renderTyping(card)
    else if (this.mode === 'recall') this.renderRecall()
    else if (this.mode === 'breakdown') this.renderBreakdown(card)
    else this.renderChoices(card)

    // จองที่เผื่อไว้ก่อนรู้ผลโหลด — เกือบทุกใบมีลำดับขีด รอให้รู้แน่ก่อนแล้ว
    // ค่อยจองจะเห็นเนื้อหากระตุกทุกครั้งที่เปลี่ยนใบ
    this.stroke.arm(this.glyphOnScreen())
  }

  // ---------- ลำดับขีด ----------

  /**
   * ตัวอักษรอยู่บนจอแล้วหรือยัง
   *
   * โหมดที่โจทย์เป็นตัวอักษรอยู่แล้ว ดูลำดับขีดไม่ได้เฉลยอะไร กดได้ตลอด
   * แต่โหมดที่โจทย์เป็นความหมาย/เสียง การเห็นตัวอักษรคือเฉลย — ต้องตอบก่อน
   */
  private glyphOnScreen(): boolean {
    const glyphIsAnswer =
      this.mode === 'romaji-to-glyph' ||
      this.mode === 'meaning-to-glyph' ||
      this.mode === 'audio-to-glyph'
    return !glyphIsAnswer || this.answered || this.peeked
  }

  /** ให้ภาพกับปุ่มตรงกับสถานะจริง — เรียกได้ทุกจังหวะที่อะไรเปลี่ยน */
  private syncStrokeBtn(): void {
    const want = this.strokeWanted && this.hasStroke && this.glyphOnScreen()
    if (want !== this.stroke.visible) {
      if (want) this.stroke.show()
      else this.stroke.hide()
    }
    this.els.strokeBtn.hidden = !this.hasStroke || !this.glyphOnScreen()
    this.els.strokeBtn.setAttribute('aria-pressed', String(this.stroke.visible))
    this.stroke.arm(!this.els.strokeBtn.hidden)
  }

  /**
   * ชิ้นส่วนที่กดแล้วเน้นได้เท่านั้นที่เป็นปุ่ม
   *
   * KRADFILE แตกละเอียดกว่า KanjiVG เพราะมันทำมาเพื่อค้นหา ไม่ใช่เพื่อเขียน
   * — 国 ที่นั่นคือ 王 囗 丶 แต่ที่นี่คือ 囗 玉 · ตัวที่ไม่ตรงกันปล่อยให้เป็น
   * ข้อความเฉย ๆ ดีกว่าเป็นปุ่มที่กดแล้วไม่มีอะไรเกิดขึ้น
   */
  private syncPartButtons(): void {
    let any = false
    for (const el of this.els.reveal.querySelectorAll<HTMLButtonElement>('.ex-part')) {
      const ch = el.querySelector('.ex-k')?.textContent ?? ''
      const ok = this.hasStroke && this.stroke.canFocus(ch)
      el.classList.toggle('is-nofocus', !ok)
      // ปิดใช้งานไปเลย ไม่ใช่แค่ทำให้ดูต่าง — ปุ่มที่กดไม่ได้ต้องไม่ติดคิว Tab
      // และต้องไม่ประกาศตัวเป็นปุ่มกดได้ให้ screen reader
      el.disabled = !ok
      any ||= ok
    }
    // ปุ่มหน้าตาเหมือนป้ายข้อความ ไม่มีอะไรบอกว่ากดได้ — บอกที่หัวข้อทีเดียว
    const label = this.els.reveal.querySelector<HTMLElement>('.parts-label')
    if (label) label.textContent = any ? 'ประกอบจาก · แตะดูขีด' : 'ประกอบจาก'
  }

  private async loadStroke(card: Card): Promise<void> {
    const ok = await this.stroke.load(card)
    // ระหว่างรอ อาจเปลี่ยนใบไปแล้ว
    if (this.current?.id !== card.id) return
    this.hasStroke = ok
    this.syncStrokeBtn()
    this.syncPartButtons()
  }

  /**
   * กดชิ้นส่วนในกล่องเฉลย → เน้นขีดของชิ้นนั้นในภาพ
   *
   * ชิ้นส่วนที่ KRADFILE บอกกับที่ KanjiVG แบ่งไม่ตรงกันเสมอ (KRADFILE แตกละเอียด
   * กว่าเพื่อการค้นหา) — ชิ้นที่เน้นไม่ได้ก็ปล่อยผ่าน ไม่ต้องขึ้นอะไรบอก
   */
  private focusPart(part: string, btn: HTMLElement): void {
    if (!this.hasStroke || !this.stroke.canFocus(part)) return
    if (!this.stroke.visible) {
      this.stroke.show()
      this.syncStrokeBtn()
    }
    const on = this.stroke.highlight(part)
    for (const el of this.els.reveal.querySelectorAll('.ex-part')) {
      const active = on && el === btn
      el.classList.toggle('is-focus', active)
      el.setAttribute('aria-pressed', String(active))
    }
  }

  private toggleStroke(): void {
    if (!this.hasStroke) return
    this.strokeWanted = !this.stroke.visible
    this.syncStrokeBtn()
  }

  // ---------- โหมดเรียน ----------

  /**
   * ใบที่ไม่เคยเห็นไม่ควรโผล่มาให้เดา — เห็นทุกอย่างพร้อมกันตรงนี้ก่อน
   * ความหมาย ชิ้นส่วนพร้อมความหมาย คำอ่าน แล้วผูกเป็นเรื่องของตัวเอง
   *
   * รูปแบบมาจาก NOTES.md §9 ที่วางตารางไว้ 3 คอลัมน์: คันจิ | ชิ้นส่วน | เรื่องที่ผูก
   * ควิซแตกชิ้นส่วนเดิมทำแค่คอลัมน์กลาง ซึ่งเป็นคอลัมน์ที่ช่วยจำน้อยที่สุด
   */
  private renderLesson(card: Card): void {
    this.els.answer.hidden = true
    this.keypad.setVisible(false)
    this.els.tiles.replaceChildren()
    this.els.tiles.className = 'tiles w tiles-single'

    // เนื้อหาทั้งหมดอยู่ในกล่องเฉลย ซึ่งเปิดค้างไว้ตั้งแต่แรก
    this.answered = true
    this.showReveal(card, { lesson: true })

    // ครั้งแรกที่เห็นตัวนี้ ควรได้ยินเสียงด้วย ไม่ต้องรอให้กดเอง
    if (this.canSpeak(card)) void this.playCurrent(card)
  }

  // ---------- โหมดนึกเอง ----------

  /**
   * ไม่มีตัวเลือกให้ตัดทิ้ง ไม่มีช่องพิมพ์ — นึกในใจแล้วเปิดเฉลยเอง
   * วิธีที่ Anki/RTK ใช้ เพราะความหมายมีหลายสำนวน ตรวจอัตโนมัติแล้วผิดบ่อยกว่าถูก
   */
  private renderRecall(): void {
    this.els.answer.hidden = true
    this.keypad.setVisible(false)

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'btn reveal-btn'
    btn.textContent = 'เปิดเฉลย (Space)'
    btn.addEventListener('click', () => this.openGrading())

    this.els.tiles.className = 'tiles w tiles-single'
    this.els.tiles.replaceChildren(btn)
  }

  /** เปิดเฉลยแล้วให้ผู้เรียนตัดสินเองว่าจำได้แค่ไหน */
  private openGrading(): void {
    const card = this.current
    if (!card || this.answered) return
    this.answered = true

    const ms = this.elapsedMs()
    this.els.tiles.replaceChildren()
    this.showReveal(card, { grading: true, ms })
  }

  // ---------- โหมดแตกชิ้นส่วน ----------

  /**
   * ตัวชี้วัด Phase 0 ที่ NOTES.md §9 ตั้งไว้ — เห็นคันจิแล้วแยกชิ้นส่วนออกได้
   * ต่างจากโหมดอื่นตรงที่ต้องเลือกให้ครบทุกชิ้น ไม่ใช่ตอบครั้งเดียวจบ
   */
  /**
   * คำอ่านสั้นๆ ของคันจิตัวอย่าง — เอา on ตัวแรกเป็นหลักเพราะคำผสมใช้ on เป็นส่วนใหญ่
   * ต้องรอ deck คันจิโหลดก่อน ยังไม่โหลดก็ไม่โชว์ ไม่ใช่บล็อกหน้าจอรอ
   */
  private lookupReading(kanji: string): string {
    const card = kanjiDeck.cards.find((c) => c.front === kanji)
    if (!card) return ''
    return this.extraList(card, 'on')[0] ?? this.extraList(card, 'kun')[0] ?? ''
  }

  /** ความหมายของชิ้นส่วน — หาจากชุด radical ที่เรียนไปแล้วเท่านั้น */
  private partMeaning(part: string): string | undefined {
    return bushuDeck.cards.find((c) => c.front === part || c.extra?.['variant'] === part)?.back
  }

  private renderBreakdown(card: Card): void {
    this.els.answer.hidden = true
    this.keypad.setVisible(false)

    const parts = this.partsOf(card)

    /*
     * ข้ามใบที่มีชิ้นส่วนนอกชุด 105 ที่เรียนไว้
     * ให้เลือก 隹 หรือ 卩 ที่ไม่เคยเห็นความหมาย = จับคู่รูปด้วยสายตา ไม่ได้เรียนอะไร
     */
    if (parts.length === 0 || !parts.every((p) => this.partMeaning(p))) {
      const remaining = this.queue.length
      if (remaining > 0) {
        this.next()
      } else {
        this.showEmpty()
        this.els.glyph.textContent = 'ชุดนี้ยังไม่มีตัวที่แตกได้ด้วย radical ที่เรียนไปแล้ว'
      }
      return
    }

    const chosen = new Set<string>()
    const wrongPicks = new Set<string>()

    // ตัวลวงมาจาก radical ที่เรียนแล้วเท่านั้น — ต้องเดาด้วยความหมายได้จริง ไม่ใช่ตัดตัวแปลกทิ้ง
    const known = bushuDeck.cards.map((c) => c.front)
    const pool = new Set(parts)
    for (let guard = 0; pool.size < parts.length + 5 && guard < 400; guard++) {
      const pick = known[Math.floor(Math.random() * known.length)]
      if (pick) pool.add(pick)
    }

    const options = [...pool]
    shuffle(options)

    this.els.tiles.className = 'tiles w'
    this.els.tiles.replaceChildren(
      ...options.map((part) => {
        const b = document.createElement('button')
        b.type = 'button'
        b.className = 'tile tile-part'
        b.dataset['answer'] = part

        // แสดงความหมายคู่กับรูปเสมอ — ไม่งั้นเป็นแบบทดสอบสายตา ไม่ใช่ความเข้าใจ
        const glyph = document.createElement('span')
        glyph.className = 'part-glyph'
        glyph.textContent = part
        glyph.style.fontFamily = fontById('sans').stack
        const meaning = document.createElement('span')
        meaning.className = 'part-meaning'
        meaning.textContent = this.partMeaning(part) ?? ''
        b.append(glyph, meaning)

        b.addEventListener('click', () => {
          if (this.answered) return
          if (parts.includes(part)) {
            chosen.add(part)
            b.classList.add('is-right')
            b.disabled = true
            if (chosen.size === parts.length) {
              // ครบแล้ว — ถือว่ารู้ถ้าไม่เคยกดผิดเลย
              this.resolve(wrongPicks.size === 0)
            }
          } else {
            wrongPicks.add(part)
            b.classList.add('is-picked-wrong')
            b.disabled = true
          }
        })
        return b
      }),
    )

    this.els.hint.textContent = ''
  }

  private pickNext(): Card | undefined {
    /*
     * โหมดที่ระบบจัดใบให้มีคิวของตัวเองแล้ว เดินตามคิวอย่างเดียวจนหมดรอบ
     * ต้องเช็คก่อน isLessonMode เพราะ this.mode ยังเป็นค่าของใบก่อนหน้าอยู่ ณ จุดนี้
     * — เคยพลาดตรงนี้แล้วคิวถูกทิ้งกลางคัน กลายเป็นสุ่มใบเดิมซ้ำไปเรื่อย ๆ
     */
    if (this.picker) return this.queue.shift()

    // โหมดเรียนกับโหมดทดสอบไม่วนซ้ำ — ผ่านครบแล้วจบ
    if (isLessonMode(this.mode) || this.probing) return this.queue.shift()

    if (this.deck.scheduling === 'drill') {
      return pickDrill(this.pool, this.drill, this.lastId)
    }

    const card = this.queue.shift()
    if (card) return card

    // คิวหมด — ทวนใบที่เรียนแล้วต่อได้ แต่ไม่ดึงใบใหม่เข้ามาเอง
    if (this.pool.length === 0) return undefined
    this.queue = buildSrsQueue(this.pool, this.progress)
    return this.queue.shift() ?? this.pool[Math.floor(Math.random() * this.pool.length)]
  }

  private showEmpty(): void {
    this.current = null
    this.els.glyph.className = 'glyph is-text'
    this.els.note.textContent = ''
    this.els.square.querySelector('.weak-badge')?.remove()
    this.els.answer.hidden = true
    this.els.reveal.hidden = true
    this.keypad.setVisible(false)
    this.els.tiles.replaceChildren()
    this.els.tiles.className = 'tiles w tiles-single'

    // จบรอบของโหมดที่ระบบเลือกใบให้ — บอกว่าชุดนี้เหลืออะไร และควรไปทำอะไรต่อ
    if (this.picker) {
      void this.showAutoDone()
      return
    }

    if (isLessonMode(this.mode)) {
      this.els.glyph.textContent = 'เรียนครบแล้วทุกใบในชุดนี้ — สลับไปโหมดฝึกได้เลย'
      return
    }

    // ทดสอบครบแล้ว — สรุปว่าผ่านกี่ใบ เหลือต้องเรียนกี่ใบ
    if (this.probing) {
      this.finishProbe()
      return
    }

    const unlearned = filterCards(this.deck, getPicked(this.deck.id)).filter((c) =>
      this.isUnseen(c),
    ).length

    if (unlearned > 0 && this.deck.modes.includes('lesson')) {
      this.els.glyph.textContent = `ยังไม่มีอะไรต้องทวน · มี ${unlearned} ใบรอเรียน`
      this.els.tiles.className = 'tiles w'
      this.els.tiles.replaceChildren(
        this.bigButton('เริ่มเรียน', 'btn', () => this.onRequestMode('lesson')),
        // ทางเข้าสำหรับคนที่มีพื้นมาก่อน — พิสูจน์ทีละใบแทนที่จะประกาศว่ารู้ทั้งชุด
        this.bigButton('เคยรู้มาบ้าง — ทดสอบก่อน', 'btn btn-ghost', () => void this.startProbe()),
      )
    } else {
      this.els.glyph.textContent = 'ไม่มีอะไรต้องทวนตอนนี้ — กลับมาใหม่ตามเวลาที่ตั้งไว้'
    }
  }

  /**
   * จอจบของโหมดอัตโนมัติ
   *
   * "ไม่มีอะไรต้องทวน" อย่างเดียวไม่พอ — คำถามถัดไปในหัวคือ *แล้วทำอะไรต่อ*
   * ตอบให้เลยว่าชุดนี้เหลือกี่ใบ ทวนรอบหน้าเมื่อไร และชุดไหนมีของรออยู่
   */
  private async showAutoDone(): Promise<void> {
    const cards = filterCards(this.deck, getPicked(this.deck.id))
    const unlearned = cards.filter((c) => this.isUnseen(c)).length
    const done = cards.length - unlearned

    /*
     * เก็บตกจนหมดแล้วไม่ควรต้องกดออกเอง — ปิดให้ แล้วบอกว่าเก็บครบ
     * ค้างไว้จะเจอจอว่างซ้ำ ๆ ทั้งที่ยังมีของให้ทวนอยู่เต็มไปหมด
     */
    if (this.weakActive && this.weakOf(cards).length === 0) {
      settings.weakOnly = false
      saveSettings()
      this.weakActive = false
      this.els.glyph.textContent = 'เก็บตกครบแล้ว — ไม่เหลือตัวที่ยังไม่แน่น'
      this.els.tiles.className = 'tiles w'
      this.els.tiles.replaceChildren(
        this.bigButton('ฝึกต่อตามปกติ', 'btn', () => this.onRequestMode('auto')),
      )
      this.onProgressChange()
      return
    }

    if (this.weakActive) {
      // ยังมีตัวอ่อนเหลือ แต่คิวรอบนี้หมด — ตอบผิดระหว่างทางก็เพิ่มตัวใหม่เข้ามาได้
      const left = this.weakOf(cards).length
      this.els.glyph.textContent = `เก็บตกรอบนี้จบแล้ว — ยังเหลือ ${left} ตัวที่ยังไม่แน่น`
    } else if (unlearned === 0) {
      this.els.glyph.textContent = `จำครบทั้ง ${done} ใบในชุดนี้แล้ว · ไม่มีอะไรค้างทวนตอนนี้`
    } else if (this.picker === 'auto') {
      /*
       * ยังมีใบใหม่แต่คิววันนี้หมด = ชนโควตาใบใหม่ต่อวัน ไม่ใช่ของหมด
       *
       * `done` คือใบที่เคยเห็นทั้งชุด ไม่ใช่ของรอบนี้ — เขียนว่า "เรียนไปแล้ว N ใบ"
       * ต่อท้าย "พอสำหรับวันนี้" จึงอ่านเป็นยอดของวันนี้ทั้งที่เป็นยอดสะสม
       */
      const newToday = this.lessonBatch
      const head = newToday > 0 ? `เรียนใหม่ ${newToday} ใบ` : 'ทวนครบแล้ว'
      this.els.glyph.textContent = `พอสำหรับวันนี้ — ${head} · เหลืออีก ${unlearned} ใบไว้วันหลัง`
    } else {
      this.els.glyph.textContent = `ทบทวนครบรอบแล้ว — ${done} ใบที่เคยเรียน`
    }

    const next = await this.nextDeckHint()
    const row: HTMLElement[] = []
    if (this.weakActive) {
      // ทางออกจากโหมดเก็บตกต้องอยู่ตรงที่มันจบ ไม่ใช่ให้ไปหาปุ่มในแถบข้างล่าง
      row.push(
        this.bigButton('ฝึกต่อตามปกติ', 'btn', () => {
          settings.weakOnly = false
          saveSettings()
          this.onRequestMode('auto')
        }),
      )
    } else if (unlearned > 0 && this.picker === 'auto') {
      // อยากเรียนต่อวันนี้ก็ได้ ไม่ต้องรอพรุ่งนี้ — แค่ไม่ใช่ค่าเริ่มต้น
      row.push(this.bigButton('เรียนต่อเลย', 'btn btn-ghost', () => this.onRequestMode('lesson')))
    }
    if (next) row.push(this.bigButton(next.label, 'btn', next.go))

    if (row.length > 0) {
      this.els.tiles.className = 'tiles w'
      this.els.tiles.replaceChildren(...row)
    }
  }

  /**
   * ชุดถัดไปที่ควรไปทำ — ชุดที่มีของค้างทวนมาก่อน ไม่มีเลยค่อยเป็นชุดที่ยังไม่เริ่ม
   * ไม่มีอะไรเหลือทั้งแอปก็ไม่ต้องเสนอ
   */
  private async nextDeckHint(): Promise<{ label: string; go: () => void } | null> {
    const now = Date.now()
    for (const pass of ['due', 'new'] as const) {
      for (const d of DECKS) {
        if (d.id === this.deck.id) continue
        await d.ensureLoaded?.()
        const cards = filterCards(d, getPicked(d.id))
        if (cards.length === 0) continue

        if (d.scheduling === 'srs') {
          const rows = await getProgressByDeck(d.id)
          const seen = new Set(rows.map((p) => p.cardId))
          if (pass === 'due') {
            const due = rows.filter((p) => p.state !== CardState.New && p.due <= now).length
            if (due > 0) return { label: `ไป ${d.titleJa} · ทวน ${due} ใบ`, go: () => this.onRequestDeck(d.id) }
          } else if (cards.some((c) => !seen.has(c.id))) {
            return { label: `เริ่ม ${d.titleJa}`, go: () => this.onRequestDeck(d.id) }
          }
        } else if (pass === 'new') {
          const stats = await getDrillAll()
          const seen = new Set(stats.filter((s) => s.seen > 0).map((s) => s.cardId))
          if (cards.some((c) => !seen.has(c.id))) {
            return { label: `เริ่ม ${d.titleJa}`, go: () => this.onRequestDeck(d.id) }
          }
        }
      }
    }
    return null
  }

  private bigButton(label: string, cls: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = cls
    b.textContent = label
    b.addEventListener('click', onClick)
    return b
  }

  // ---------- ทดสอบก่อนเรียน ----------

  /** เริ่มทดสอบใบที่ยังไม่เคยเห็น — ใช้โหมดตอบปกติ ไม่ใช่โหมดใหม่ */
  async startProbe(): Promise<void> {
    this.probing = true
    this.probed.clear()
    const mode = this.deck.modes.find((m) => !isLessonMode(m)) ?? this.mode
    await this.start(this.deck, mode)
    // ให้แถบสรุปกับตัวนับรู้ว่าเปลี่ยนสถานะแล้ว
    this.onProgressChange()
  }

  private finishProbe(): void {
    const passed = this.stats.right
    const failed = this.probed.size - passed
    this.probing = false

    this.els.glyph.className = 'glyph is-text'
    this.els.glyph.textContent =
      failed > 0
        ? `ผ่านแล้ว ${passed} ใบ · ต้องเรียนอีก ${failed} ใบ`
        : `ผ่านหมดทั้ง ${passed} ใบ`

    this.els.tiles.className = 'tiles w'
    this.els.tiles.replaceChildren(
      failed > 0
        ? this.bigButton(`ไปเรียน ${failed} ใบที่เหลือ`, 'btn', () => this.onRequestMode('lesson'))
        : this.bigButton('เริ่มทวน', 'btn', () => void this.reload()),
    )
  }

  /** กำลังทดสอบอยู่ไหม — ใช้ปรับข้อความบนแถบสรุป */
  isProbing(): boolean {
    return this.probing
  }

  // ---------- แสดงคำถาม ----------

  private renderPrompt(card: Card): void {
    const g = this.els.glyph
    g.style.fontFamily = ''

    switch (this.mode) {
      case 'glyph-to-romaji': {
        const font = pickFont(new Set(settings.fonts))
        g.className = 'glyph'
        g.style.fontFamily = font.stack
        g.textContent = card.front
        this.els.note.textContent = settings.fonts.length > 1 ? font.label : ''
        break
      }
      case 'audio-to-glyph':
        g.className = 'glyph is-text'
        g.textContent = '🔊'
        g.style.fontFamily = ''
        this.els.note.textContent = 'ฟังแล้วเลือกตัวที่ตรง'
        void this.playCurrent(card)
        break
      case 'romaji-to-glyph':
        g.className = 'glyph is-romaji'
        g.textContent = card.back
        this.els.note.textContent = 'เลือกตัวที่ตรง'
        break
      case 'glyph-to-meaning': {
        g.className = 'glyph'
        g.style.fontFamily = fontById('sans').stack
        g.textContent = card.front
        const variant = this.extraString(card, 'variant')
        this.els.note.textContent = variant ? `รูปแปร ${variant}` : ''
        break
      }
      case 'meaning-to-glyph':
        g.className = 'glyph is-text'
        g.textContent = card.back
        this.els.note.textContent = 'เลือก radical ที่ตรง'
        break
      case 'recall':
        g.className = 'glyph'
        g.style.fontFamily = fontById('sans').stack
        g.textContent = card.front
        this.els.note.textContent = 'นึกให้ได้ก่อน แล้วค่อยเปิดเฉลย'
        break
      case 'breakdown':
        g.className = 'glyph'
        g.style.fontFamily = fontById('sans').stack
        g.textContent = card.front
        this.els.note.textContent = `เลือกให้ครบ ${this.partsOf(card).length} ชิ้น`
        break
      case 'lesson':
        g.className = 'glyph'
        g.style.fontFamily = fontById('sans').stack
        g.textContent = card.front
        this.els.note.textContent = 'ใบใหม่'
        break
    }

    /*
     * ยิ่งหลายตัวยิ่งต้องเล็กลง ไม่งั้นล้นออกนอกช่อง — yōon เป็น 2 ตัว (ちゃ)
     * ส่วนคำศัพท์ยาวได้ถึง 7 ตัว (第二次世界大戦)
     *
     * ส่งจำนวนตัวให้ CSS หารเอง แทนที่จะแบ่งเป็นช่วง ๆ เพราะช่วงตายตัวพังทันที
     * ที่เจอคำยาวกว่าที่ช่วงสุดท้ายเผื่อไว้ · ลำดับขีดใช้ตัวเดียวกันจึงย่อตามกันเสมอ
     */
    const glyphLen = [...g.textContent].length
    this.els.square.style.setProperty('--n', String(Math.max(1, glyphLen)))

    this.els.audioBtn.hidden = !this.canSpeak(card)
    this.showWeakBadge(card)
  }

  /**
   * บอกว่าใบนี้โผล่มาเพราะอะไร
   *
   * เหตุผลต่างกันแก้คนละแบบ — "ถูกแต่ช้า" คือฝึกให้เร็วขึ้น ส่วน "ผิดซ้ำ" คือ
   * ทวนเท่าไรก็ไม่ช่วย ต้องเปลี่ยนเรื่องที่ผูกไว้ · ระบบรู้อยู่แล้ว ไม่บอกก็เสียของ
   */
  private showWeakBadge(card: Card): void {
    const old = this.els.square.querySelector('.weak-badge')
    old?.remove()
    if (isLessonMode(this.mode)) return

    const w = this.weakOf([card])[0]
    if (!w || w.reason === 'ok' || w.reason === 'new') return

    const b = document.createElement('span')
    b.className = `weak-badge is-${w.reason}`
    b.textContent = REASON_LABEL[w.reason]
    this.els.square.append(b)
  }

  // ---------- โหมดพิมพ์ ----------

  private renderTyping(card: Card): void {
    this.els.answer.hidden = false
    this.els.tiles.replaceChildren()

    const input = this.els.input
    input.value = ''
    input.disabled = false
    input.classList.remove('is-wrong')
    input.maxLength = maxAnswerLength(card) + 2

    this.applyInputMode()
  }

  /** เลือกระหว่าง keyboard ระบบกับ keypad ในแอป — เรียกใหม่ได้เมื่ออุปกรณ์เปลี่ยน */
  applyInputMode(): void {
    if (!isTypingMode(this.mode)) {
      this.keypad.setVisible(false)
      return
    }
    const useKeypad = settings.keypad ?? shouldUseKeypad()
    this.keypad.setVisible(useKeypad)
    // keypad เปิดอยู่ = ตั้ง readOnly กัน keyboard ระบบเด้งขึ้นมาบังจอ
    this.els.input.readOnly = useKeypad
    if (!useKeypad && !this.answered) this.els.input.focus()
  }

  private appendInput(text: string): void {
    if (this.answered) return
    this.els.input.value += text
    this.checkTyped()
  }

  private backspace(): void {
    if (this.answered) return
    this.els.input.value = this.els.input.value.slice(0, -1)
    this.els.input.classList.remove('is-wrong')
  }

  private checkTyped(): void {
    const card = this.current
    if (!card || this.answered) return

    const verdict = judge(this.els.input.value, card)
    if (verdict === 'correct') this.resolve(true)
    else if (verdict === 'wrong') {
      this.els.input.classList.add('is-wrong')
      this.resolve(false)
    } else {
      this.els.input.classList.remove('is-wrong')
    }
  }

  // ---------- โหมดเลือก ----------

  private renderChoices(card: Card): void {
    this.els.answer.hidden = true
    this.keypad.setVisible(false)

    const showGlyph =
      this.mode === 'meaning-to-glyph' ||
      this.mode === 'romaji-to-glyph' ||
      this.mode === 'audio-to-glyph'
    const label = (c: Card): string => (showGlyph ? c.front : c.back)

    const options = [card, ...this.distractors(card, label)]
    shuffle(options)

    this.els.tiles.className = `tiles w${showGlyph ? ' glyph-grid' : ''}`
    this.els.tiles.replaceChildren(
      ...options.map((o, i) => {
        const b = document.createElement('button')
        b.type = 'button'
        b.className = `tile${showGlyph ? ' glyph-mode' : ''}`
        b.dataset.answer = label(o)

        const num = document.createElement('span')
        num.className = 'num'
        num.textContent = String(i + 1)

        const text = document.createElement('span')
        text.textContent = label(o)
        if (showGlyph) text.style.fontFamily = fontById('sans').stack

        b.append(num, text)
        b.addEventListener('click', () => this.pickChoice(o, b))
        return b
      }),
    )
  }

  private distractors(card: Card, label: (c: Card) => string): Card[] {
    const used = new Set([label(card)])
    const out: Card[] = []
    /*
     * pool เล็กเกินทำตัวเลือกไม่ครบ 4 ตัว ต้องถอยไปหาที่อื่น — แต่ถอยไปทั้ง deck
     * จะดึงใบที่ไม่ได้เลือกไว้เข้ามาด้วย ตัวลวงจึงเคยเป็นคันจิชื่อคนที่ยังไม่มี
     * คำแปลไทย โผล่เป็นภาษาอังกฤษปนอยู่ตัวเดียวในสี่ตัว
     */
    const source = this.pool.length >= CHOICE_COUNT + 1 ? this.pool : this.selected

    // สุ่มจาก pool เดียวกับที่ฝึกอยู่ ตัวเลือกจะได้ใกล้เคียงกัน ไม่ใช่เดาง่าย
    for (let guard = 0; out.length < CHOICE_COUNT - 1 && guard < 500; guard++) {
      const pick = source[Math.floor(Math.random() * source.length)]
      if (!pick || pick.id === card.id) continue
      const l = label(pick)
      if (used.has(l)) continue
      used.add(l)
      out.push(pick)
    }
    return out
  }

  private pickChoice(chosen: Card, btn: HTMLButtonElement): void {
    if (this.answered || !this.current) return
    const ok = chosen.id === this.current.id
    const correctLabel =
      this.mode === 'meaning-to-glyph' || this.mode === 'romaji-to-glyph'
        ? this.current.front
        : this.current.back

    for (const el of this.els.tiles.querySelectorAll<HTMLButtonElement>('.tile')) {
      el.disabled = true
      if (el.dataset.answer === correctLabel) el.classList.add('is-right')
    }
    if (!ok) btn.classList.add('is-picked-wrong')

    this.resolve(ok)
  }

  // ---------- แอบดูเฉลย ----------

  /**
   * เปิดเฉลยก่อนตอบ — DJT ให้ hover ดูได้ เพราะตอนเพิ่งเริ่มยังไม่รู้สักตัว
   * การบังคับให้ตอบผิดก่อนถึงจะเห็นเฉลยมันฝืนและไม่ได้สอนอะไร
   *
   * แต่ดูแล้วตอบถูกไม่นับว่ารู้ — ไม่งั้นสถิติโกหกตัวเอง
   */
  private showPeek(): void {
    const card = this.current
    if (!card || this.answered || this.peeked) return
    this.peeked = true

    const answer = this.deck.id === 'kana' ? bestAnswer(card) : card.back
    // โหมดที่โจทย์เป็นความหมาย/เสียงอยู่แล้ว → เฉลยคือตัวอักษร
    const showsGlyph = this.mode === 'meaning-to-glyph' || this.mode === 'romaji-to-glyph'
    this.els.peek.textContent = showsGlyph ? card.front : answer
    this.els.peek.classList.toggle('is-jp', showsGlyph)
    this.els.square.classList.add('is-peek')
    this.syncStrokeBtn()

    if (this.canSpeak(card)) void this.playCurrent(card)
  }

  // ---------- ตัดสิน + บันทึก ----------

  /**
   * @param typedRight ตอบตรงคำตอบไหม (เรื่องของการแสดงผล)
   *
   * "ตอบตรง" กับ "รู้จริง" ไม่ใช่เรื่องเดียวกัน — แอบดูเฉลยแล้วพิมพ์ตามก็ตรง
   * แต่ยังไม่รู้ ต้องได้เจออีก ไม่งั้นสถิติโกหกตัวเอง
   */
  private resolve(typedRight: boolean): void {
    const card = this.current
    if (!card || this.answered) return
    this.answered = true

    const knew = typedRight && !this.peeked

    const ms = this.elapsedMs()
    // แอบดูแล้วพิมพ์ตรง ไม่ต้องขึ้นกากบาท — ไม่ได้ตอบผิด แค่ยังไม่รู้
    if (typedRight) {
      if (knew) this.els.square.classList.add('is-correct')
    } else {
      this.els.square.classList.add('is-wrong')
    }
    this.els.input.disabled = true
    this.syncStrokeBtn()

    this.stats.total++
    if (knew) {
      this.stats.right++
      this.stats.streak++
      this.stats.best = Math.max(this.stats.best, this.stats.streak)
    } else {
      this.stats.streak = 0
    }

    if (this.probing) {
      this.probed.add(card.id)
      // ตอบผิดตอนทดสอบ = ไม่บันทึกอะไรเลย ใบนั้นจึงยังเป็น "ยังไม่เคยเห็น"
      // และรอเราอยู่ในคิวเรียน — ไม่ต้องลบอะไรย้อนหลัง
      if (knew) void this.record(card, true, ms)
    } else {
      void this.record(card, knew, ms)
    }

    if (settings.autoAudio && this.canSpeak(card)) void this.playCurrent(card)

    if (typedRight) {
      // เห็นคำตอบแล้วไม่ว่าทางไหน — ไม่มีอะไรต้องเรียนต่อ ไปเลย
      this.next()
    } else {
      this.showReveal(card)
    }
  }

  private async record(card: Card, correct: boolean, ms: number, forced?: Rating): Promise<void> {
    const now = Date.now()
    // โหมดนึกเองให้ผู้เรียนเลือกเกรดเอง — โหมดอื่นแปลงถูก/ผิดเป็น Good/Again
    const rating: Rating = forced ?? (correct ? 3 : 1)

    let stateBefore = CardState.New

    if (this.deck.scheduling === 'drill') {
      const stat = updateDrill(card.id, this.drill.get(card.id), correct, ms, now)
      this.drill.set(card.id, stat)
      await putDrill(stat)
      stateBefore = stat.seen > 1 ? CardState.Review : CardState.New
    } else {
      const before = this.progress.get(card.id) ?? newProgress(card.id, this.deck.id, now)
      stateBefore = before.state
      const scheduled = schedule(before, rating, now)
      /*
       * ค่าเฉลี่ยแบบวิ่ง ถ่วงให้ครั้งล่าสุดมีน้ำหนักกว่า — ความเร็วเปลี่ยนได้เมื่อเริ่มคล่อง
       * นับเฉพาะตอบถูกและอยู่ในเวลาที่เชื่อได้ ตรงกับกฎฝั่ง drill (ดู updateDrill)
       */
      const prevMs = before.avgMs ?? 0
      const usable = correct && ms <= MAX_ANSWER_MS
      const avgMs = usable ? (prevMs > 0 ? Math.round(prevMs * 0.7 + ms * 0.3) : ms) : prevMs
      const after = { ...scheduled, ...(avgMs > 0 ? { avgMs } : {}) }
      this.progress.set(card.id, after)
      await putProgress(after)
    }

    // เก็บทุก deck ไม่ใช่แค่ SRS — ประวัติระยะยาวคือสิ่งที่ตอบได้ว่าตัวไหนกวนใจมาตลอด
    await addLog({
      cardId: card.id,
      deck: this.deck.id,
      ts: now,
      rating,
      ms,
      mode: this.mode,
      stateBefore,
    })

    this.onProgressChange()
  }

  // ---------- เฉลย ----------

  private showReveal(
    card: Card,
    opts?: { grading?: true; lesson?: true; ms?: number },
  ): void {
    const r = this.els.reveal
    r.replaceChildren()

    const line = document.createElement('div')
    line.className = 'reveal-line'

    const glyph = document.createElement('span')
    glyph.className = 'reveal-glyph'
    glyph.textContent = card.front
    line.append(glyph)

    const variant = this.extraString(card, 'variant')
    if (variant) {
      const v = document.createElement('span')
      v.className = 'reveal-var'
      v.textContent = `· ${variant}`
      line.append(v)
    }

    if (this.deck.id === 'kana') {
      const romaji = document.createElement('span')
      romaji.className = 'reveal-romaji'
      romaji.textContent = bestAnswer(card)
      line.append(romaji)

      const pair = this.extraString(card, 'pair')
      if (pair) {
        const p = document.createElement('span')
        p.className = 'reveal-var'
        p.textContent = `คู่: ${pair}`
        line.append(p)
      }
    } else {
      const th = document.createElement('span')
      th.className = 'reveal-th'
      th.textContent = this.override?.keyword || card.back
      line.append(th)
    }

    r.append(line)

    // คำศัพท์มีคำอ่านของทั้งคำอยู่แล้ว ไม่ต้องแจกแจง on/kun ทีละตัว
    if (this.deck.id === 'vocab') {
      const w = this.wordLine(card)
      if (w) r.append(w)
    } else {
      // คำอ่าน + จำนวนขีด (คันจิ) — kanjidic ให้มาแล้ว ไม่โชว์ก็เสียของ
      const readings = this.readingLine(card)
      if (readings) r.append(readings)
    }

    // ชิ้นส่วนที่ประกอบ — หัวใจของการเรียนแบบ radical
    const parts = this.partsOf(card)
    if (parts.length > 0) {
      r.append(this.partsLine(parts))
      // ภาพอาจยังโหลดไม่เสร็จ — loadStroke เรียกซ้ำให้อีกทีตอนพร้อม
      this.syncPartButtons()
    }

    const examples = this.examples(card)
    if (examples.length > 0) {
      const exLine = document.createElement('div')
      exLine.className = 'reveal-line'
      const label = document.createElement('span')
      label.className = 'reveal-label'
      label.textContent = 'อยู่ในคันจิ'
      const box = document.createElement('div')
      box.className = 'examples'
      for (const { kanji, meaning } of examples) {
        const d = document.createElement('div')
        d.className = 'ex ex-stacked'
        const k = document.createElement('span')
        k.className = 'ex-k'
        k.textContent = kanji

        const info = document.createElement('span')
        info.className = 'ex-info'
        // คำอ่านของคันจิตัวอย่าง — นี่คือจุดที่ต่อยอดไปจำศัพท์ได้จริง
        const reading = settings.showReadings ? this.lookupReading(kanji) : ''
        if (reading) {
          const rd = document.createElement('span')
          rd.className = 'ex-reading'
          rd.textContent = reading
          info.append(rd)
        }
        const t = document.createElement('span')
        t.className = 'ex-t'
        t.textContent = meaning
        info.append(t)

        d.append(k, info)
        box.append(d)
      }
      exLine.append(label, box)
      r.append(exLine)
    }

    // เรื่องที่แต่งเอง — NOTES.md §9 บอกว่าอันนี้คือตัวที่ทำให้จำติดจริง
    // แต่แสดงเฉพาะใบที่มีเรื่องแล้วหรือถึงเวลาต้องแต่ง ที่เหลืออยู่หลังปุ่มเล็ก
    const mnemo = this.mnemonicBlock(card)
    if (mnemo) r.append(mnemo)
    else line.append(this.mnemonicAddBtn(card))

    const p = this.progress.get(card.id)
    if (p && isLeech(p)) {
      const warn = document.createElement('div')
      warn.className = 'note'
      warn.style.color = 'var(--shu)'
      warn.textContent = `ผิดมา ${p.lapses} ครั้งแล้ว — ตัวนี้ควรแต่งเรื่องใหม่ ไม่ใช่ทวนซ้ำ`
      r.append(warn)
    }

    if (opts?.grading) {
      r.append(this.gradeRow(card, opts.ms ?? 0))
      r.hidden = false
      return
    }

    const row = document.createElement('div')
    row.className = 'next-row'
    const btn = document.createElement('button')
    btn.className = 'btn'

    if (opts?.lesson) {
      // ไม่ใช่การตอบถูก — แค่ยอมรับว่าดูแล้ว เข้าใจแล้ว พร้อมให้ทวน
      btn.textContent = 'เข้าใจแล้ว · ให้ไปอยู่คิวทวน'
      btn.addEventListener('click', () => void this.finishLesson(card))
    } else {
      btn.textContent = 'ถัดไป →'
      btn.addEventListener('click', () => this.next())
    }

    row.append(btn)
    r.append(row)
    r.hidden = false
    btn.focus()
  }

  /** จบบทเรียนของใบนี้ — เข้าคิวทวนโดยไม่นับเป็นคำตอบถูก */
  private async finishLesson(card: Card): Promise<void> {
    const now = Date.now()
    let stateBefore = CardState.New

    if (this.deck.scheduling === 'drill') {
      // drill ไม่มี state — นับว่าเคยเห็นแล้วด้วย seen ที่ยังไม่ผ่านการทดสอบ
      const stat = this.drill.get(card.id) ?? {
        cardId: card.id,
        seen: 0,
        wrong: 0,
        streak: 0,
        avgMs: 0,
        lastWrongAt: 0,
        updatedAt: now,
      }
      const seenOnce = { ...stat, seen: stat.seen + 1, updatedAt: now }
      this.drill.set(card.id, seenOnce)
      await putDrill(seenOnce)
    } else {
      const before = this.progress.get(card.id) ?? newProgress(card.id, this.deck.id, now)
      stateBefore = before.state
      // ให้ Good เพื่อเริ่มนับช่วงทวน แต่ไม่แตะสถิติ session เพราะไม่ได้ทดสอบอะไร
      const after = schedule(before, 3, now)
      this.progress.set(card.id, after)
      await putProgress(after)
    }

    await addLog({
      cardId: card.id,
      deck: this.deck.id,
      ts: now,
      rating: 3,
      ms: this.elapsedMs(),
      mode: 'lesson',
      stateBefore,
    })

    this.onProgressChange()
    /*
     * โหมดเรียนวัดความคืบหน้าจาก pool ที่ร่อยหรอลง — เรียนใบไหนแล้วก็เอาออก
     * แต่ในโหมดอัตโนมัติ pool คือขนาดของคิวทั้งรอบ ซึ่งไม่ควรขยับตอนเรียนใบใหม่
     * ไม่งั้น "คิววันนี้ 14 ใบ" ค่อย ๆ กลายเป็น 13, 12 ทั้งที่ยังไม่ได้ทำอะไรผิด
     */
    if (isLessonMode(this.mode) && !this.picker) {
      this.pool = this.pool.filter((c) => c.id !== card.id)
    }
    this.next()
  }

  /**
   * on/kun + จำนวนขีด สำหรับ deck คันจิ
   *
   * ไม่มี romaji กำกับโดยตั้งใจ — คำอ่านที่เป็น kana ล้วนคือการฝึกอ่าน kana ในบริบทจริง
   * ใส่ romaji เมื่อไร ตาจะไปเกาะ romaji แล้ว kana ไม่มีวันคล่อง
   * (on เขียน katakana · kun เขียน hiragana เป็น convention ของพจนานุกรม — NOTES.md §4)
   */
  private readingLine(card: Card): HTMLElement | null {
    if (!settings.showReadings) return null

    const on = this.extraList(card, 'on')
    const kun = this.extraList(card, 'kun')
    const strokes = Number(card.extra?.['strokes'] ?? 0)
    if (on.length === 0 && kun.length === 0) return null

    const el = document.createElement('div')
    el.className = 'reveal-line reveal-readings'

    const group = (label: string, list: string[], cls: string): HTMLElement => {
      const wrap = document.createElement('span')
      wrap.className = 'rd-group'
      const lb = document.createElement('span')
      lb.className = 'reveal-label'
      lb.textContent = label
      const val = document.createElement('span')
      val.className = `rd ${cls}`
      val.textContent = list.join('・')
      wrap.append(lb, val)
      return wrap
    }

    // kun เป็นคำที่พูดได้เลย จึงแสดงเป็นคำเต็มพร้อมคำอ่าน ไม่ใช่ `た.べる` ดิบ ๆ
    // on เป็นเสียงที่ไม่โผล่เดี่ยว ๆ (ต้องอยู่ในคำผสม) แสดงเป็นคาตาคานะตามเดิม
    const words = kunWords(card.front, kun)
    if (words.length > 0) el.append(this.kunGroup(words))
    else if (kun.length > 0) el.append(group('kun', kun, 'rd-kun'))
    if (on.length > 0) el.append(group('on', on, 'rd-on'))
    if (strokes > 0) {
      const s = document.createElement('span')
      s.className = 'rd-group'
      s.innerHTML = `<span class="reveal-label">ขีด</span> <span class="rd">${strokes}</span>`
      el.append(s)
    }
    return el
  }

  /**
   * บรรทัดข้อมูลของการ์ดคำศัพท์ — คำอ่าน ชนิดคำ แล้วประโยคที่คนแต่งไว้จริง
   *
   * ประโยคสำคัญกว่าที่คิด: คำเดี่ยว ๆ ลอยอยู่จำยากกว่าคำที่เห็นทำงานอยู่ในประโยค
   * และเป็นตัวบอกว่าคำนี้ใช้ยังไง ซึ่งความหมายไทยคำเดียวบอกไม่ได้
   */
  private wordLine(card: Card): HTMLElement | null {
    const reading = this.extraString(card, 'reading')
    const posTh = this.extraString(card, 'posTh')
    const sentence = this.extraString(card, 'sentence')
    if (!reading && !sentence) return null

    const el = document.createElement('div')
    el.className = 'reveal-line reveal-readings'

    if (reading) {
      const wrap = document.createElement('span')
      wrap.className = 'rd-group'
      const lb = document.createElement('span')
      lb.className = 'reveal-label'
      lb.textContent = 'อ่าน'
      const val = document.createElement('span')
      val.className = 'rd rd-kun'
      val.textContent = reading
      wrap.append(lb, val)
      el.append(wrap)
    }
    if (posTh) {
      const p = document.createElement('span')
      p.className = 'rd-group'
      p.innerHTML = `<span class="reveal-label">ชนิด</span> <span class="pos-th">${posTh}</span>`
      el.append(p)
    }

    if (!sentence) return el

    const box = document.createElement('div')
    box.className = 'reveal-line sentence-line'
    const lb = document.createElement('span')
    lb.className = 'reveal-label'
    lb.textContent = 'ตัวอย่าง'
    const s = document.createElement('span')
    s.className = 'sentence'
    // ไฮไลต์คำที่กำลังเรียนในประโยค ให้ตาจับได้ว่ามันอยู่ตรงไหน
    const at = sentence.indexOf(card.front)
    if (at >= 0) {
      s.append(sentence.slice(0, at))
      const hit = document.createElement('b')
      hit.textContent = card.front
      s.append(hit, sentence.slice(at + card.front.length))
    } else {
      s.textContent = sentence
    }
    /*
     * ประโยคที่อ่านไม่ออกคือแถวตัวอักษรเปล่า ๆ ไม่ได้สอนอะไร — คำแปลคือสิ่งที่ทำให้
     * เห็นว่าคำที่เพิ่งเรียนทำหน้าที่อะไรอยู่ในนั้น
     *
     * Tatoeba ไม่มีคำแปลไทยให้ (6 จาก 2,430 ประโยคที่ deck ใช้) จึงเขียนเองใน
     * data-th/sentences/ — คำที่ยังไม่ได้แปลก็แค่ไม่ขึ้นบรรทัดนี้
     *
     * ซ้อนอยู่ในคอลัมน์เดียวกับตัวประโยค ไม่ใช่บรรทัดใหม่ที่เยื้องเอาเอง —
     * ความกว้างของป้ายเปลี่ยนตามฟอนต์ เดาไว้เมื่อไรก็เหลื่อมเมื่อนั้น
     */
    const col = document.createElement('div')
    col.className = 'sentence-col'
    col.append(s)

    const th = this.extraString(card, 'sentenceTh')
    if (th) {
      const t = document.createElement('span')
      t.className = 'sentence-th'
      t.textContent = th
      col.append(t)
    }

    box.append(lb, col)

    // ประโยคเต็มมีจังหวะและพิทช์ที่คำเดี่ยวไม่มี — ฟังทั้งประโยคได้ยินว่าคำนี้วางตรงไหน
    if (hasJapaneseVoice()) {
      const play = document.createElement('button')
      play.type = 'button'
      play.className = 'sentence-play'
      play.textContent = '🔊'
      play.title = 'ฟังทั้งประโยค'
      play.setAttribute('aria-label', 'ฟังประโยคตัวอย่าง')
      play.addEventListener('click', () => speakText(sentence))
      box.append(play)
    }

    const wrap = document.createElement('div')
    wrap.style.display = 'contents'
    wrap.append(el, box)
    return wrap
  }

  /** คำ kun แต่ละคำ: รูปเขียนตัวใหญ่ คำอ่านตัวเล็กใต้ลงมา */
  private kunGroup(words: KunWord[]): HTMLElement {
    const wrap = document.createElement('span')
    wrap.className = 'rd-group'
    const lb = document.createElement('span')
    lb.className = 'reveal-label'
    lb.textContent = 'kun'
    const box = document.createElement('span')
    box.className = 'kun-words'
    for (const { word, reading } of words) {
      const w = document.createElement('span')
      w.className = 'kun-word'
      const jp = document.createElement('span')
      jp.className = 'kun-jp'
      jp.textContent = word
      const rd = document.createElement('span')
      rd.className = 'kun-rd'
      rd.textContent = reading
      w.append(jp, rd)
      box.append(w)
    }
    wrap.append(lb, box)
    return wrap
  }

  /** ชิ้นส่วนที่ประกอบ พร้อมความหมายของชิ้นที่เรารู้จักแล้ว */
  private partsLine(parts: string[]): HTMLElement {
    const el = document.createElement('div')
    el.className = 'reveal-line'
    const label = document.createElement('span')
    label.className = 'reveal-label parts-label'
    label.textContent = 'ประกอบจาก'
    const box = document.createElement('div')
    box.className = 'examples'

    for (const part of parts) {
      const known = bushuDeck.cards.find((c) => c.front === part || c.extra?.['variant'] === part)
      // ปุ่มเสมอ ไม่ใช่ div — กดแล้วเน้นว่าชิ้นนี้เป็นขีดไหนของตัวเต็ม
      const d = document.createElement('button')
      d.type = 'button'
      d.className = known ? 'ex ex-part' : 'ex ex-unknown ex-part'
      const k = document.createElement('span')
      k.className = 'ex-k'
      k.textContent = part
      const t = document.createElement('span')
      t.className = 'ex-t'
      t.textContent = known ? known.back : '—'
      d.append(k, t)
      d.setAttribute('aria-pressed', 'false')
      d.addEventListener('click', () => this.focusPart(part, d))
      box.append(d)
    }
    el.append(label, box)
    return el
  }

  /** 4 ปุ่มเกรดพร้อมบอกว่าจะเจออีกทีเมื่อไร */
  private gradeRow(card: Card, ms: number): HTMLElement {
    const row = document.createElement('div')
    row.className = 'grades'

    const base = this.progress.get(card.id) ?? newProgress(card.id, this.deck.id)
    const preview = previewIntervals(base)
    const LABELS: Record<Rating, string> = { 1: 'ไม่รู้', 2: 'ยาก', 3: 'ได้', 4: 'ง่าย' }

    for (const rating of [1, 2, 3, 4] as const) {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = 'grade'
      b.dataset['r'] = String(rating)
      const main = document.createElement('span')
      main.textContent = `${rating} ${LABELS[rating]}`
      const sub = document.createElement('small')
      sub.textContent = formatInterval(preview[rating])
      b.append(main, sub)
      b.addEventListener('click', () => this.applyGrade(card, rating, ms))
      row.append(b)
    }
    return row
  }

  private applyGrade(card: Card, rating: Rating, ms: number): void {
    const knew = rating >= 3 && !this.peeked

    this.stats.total++
    if (knew) {
      this.stats.right++
      this.stats.streak++
      this.stats.best = Math.max(this.stats.best, this.stats.streak)
    } else {
      this.stats.streak = 0
    }

    void this.record(card, knew, ms, this.peeked ? 1 : rating)
    this.next()
  }

  /** กล่องเรื่องที่แต่งเอง — แก้ได้ตรงนั้นเลย ไม่ต้องออกไปหน้าอื่น */
  /**
   * กล่องแต่งเรื่อง — แสดงเฉพาะตอนที่มันมีค่า
   *
   * เคยโผล่ทุกใบพร้อมคำชวน "ยังไม่ได้แต่งเรื่อง" ซึ่งอ่านเหมือนงานค้างที่ไม่มีวันหมด
   * ตอนเรียนใบใหม่ 15 ใบรวดไม่มีใครหยุดพิมพ์ไทย 15 รอบ ยิ่งบนมือถือ · จังหวะที่
   * mnemonic ได้ผลจริงคือใบที่ผิดซ้ำจนทวนเท่าไรก็ไม่ติด ตรงนั้นถึงคุ้มที่จะหยุดคิด
   *
   * ใบปกติที่อยากแต่งเองยังมีปุ่มเล็กท้ายบรรทัดแรกให้เรียก
   */
  private mnemonicBlock(card: Card): HTMLElement | null {
    const text = this.override?.mnemonic ?? ''
    const p = this.progress.get(card.id)
    const overdue = !!p && isLeech(p)
    if (!text && !overdue) return null

    const box = document.createElement('div')
    box.className = 'mnemo'

    const view = document.createElement('div')
    view.className = text ? 'mnemo-text' : 'mnemo-text is-empty'
    view.textContent = text || 'ยิ่งเว่อร์ยิ่งจำติด — แต่งเองเท่านั้นถึงจะอยู่'

    const edit = document.createElement('button')
    edit.type = 'button'
    edit.className = 'mnemo-edit'
    edit.textContent = text ? 'แก้' : '+ แต่งเรื่อง'
    edit.addEventListener('click', () => this.openMnemonicEditor(card, box))

    box.append(view, edit)
    return box
  }

  /** ปุ่มเรียกกล่องแต่งเรื่องสำหรับใบที่ยังไม่มีเรื่องและยังไม่ถึงขั้นผิดซ้ำ */
  private mnemonicAddBtn(card: Card): HTMLButtonElement {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = 'mnemo-add'
    b.textContent = '+ เรื่อง'
    b.title = 'แต่งเรื่องช่วยจำ (M)'
    b.addEventListener('click', () => this.openMnemonic(card))
    return b
  }

  /** เปิดกล่องแต่งเรื่องของใบปัจจุบัน — สร้างขึ้นมาถ้ายังไม่มี */
  private openMnemonic(card: Card): void {
    const existing = this.els.reveal.querySelector<HTMLElement>('.mnemo')
    if (existing) {
      this.openMnemonicEditor(card, existing)
      return
    }
    const box = document.createElement('div')
    box.className = 'mnemo'
    this.els.reveal.querySelector('.mnemo-add')?.remove()
    // วางก่อนแถวปุ่มถัดไป เพื่อให้ปุ่มยังอยู่ล่างสุดเหมือนเดิม
    const row = this.els.reveal.querySelector('.next-row, .grades')
    if (row) row.before(box)
    else this.els.reveal.append(box)
    this.openMnemonicEditor(card, box)
  }

  private openMnemonicEditor(card: Card, box: HTMLElement): void {
    box.replaceChildren()

    const ta = document.createElement('textarea')
    ta.className = 'mnemo-input'
    ta.rows = 2
    ta.placeholder = `${card.front} = ...`
    ta.value = this.override?.mnemonic ?? ''

    const kw = document.createElement('input')
    kw.className = 'mnemo-keyword'
    kw.type = 'text'
    kw.placeholder = `คำสั้นๆ ของตัวเอง (ตอนนี้: ${card.back})`
    kw.value = this.override?.keyword ?? ''

    const save = document.createElement('button')
    save.type = 'button'
    save.className = 'btn'
    save.textContent = 'เก็บไว้'
    save.addEventListener('click', () => {
      void this.saveOverride(card, kw.value.trim(), ta.value.trim(), box)
    })

    // Ctrl+Enter เก็บเลย — พิมพ์เสร็จไม่ต้องละมือไปหาเมาส์
    ta.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        void this.saveOverride(card, kw.value.trim(), ta.value.trim(), box)
        e.preventDefault()
      }
      e.stopPropagation()
    })
    kw.addEventListener('keydown', (e) => e.stopPropagation())

    box.append(kw, ta, save)
    ta.focus()
  }

  private async saveOverride(
    card: Card,
    keyword: string,
    mnemonic: string,
    box: HTMLElement,
  ): Promise<void> {
    const next: CardOverride = { cardId: card.id, updatedAt: Date.now() }
    if (keyword) next.keyword = keyword
    if (mnemonic) next.mnemonic = mnemonic

    await putOverride(next)
    this.override = next

    // เก็บโดยไม่ได้พิมพ์เรื่อง (เช่นแก้แต่ keyword) — กล่องไม่ต้องค้างไว้
    const block = this.mnemonicBlock(card)
    if (block) {
      box.replaceWith(block)
    } else {
      box.remove()
      const line = this.els.reveal.querySelector('.reveal-line')
      if (line && !line.querySelector('.mnemo-add')) line.append(this.mnemonicAddBtn(card))
    }
  }

  // ---------- ตัวช่วย ----------

  private extraList(card: Card, key: string): string[] {
    const v = card.extra?.[key]
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  }

  private extraString(card: Card, key: string): string {
    const v = card.extra?.[key]
    return typeof v === 'string' ? v : ''
  }

  private examples(card: Card): Array<{ kanji: string; meaning: string }> {
    const v = card.extra?.['examples']
    if (!Array.isArray(v)) return []
    return v.filter(
      (x): x is { kanji: string; meaning: string } =>
        typeof x === 'object' && x !== null && 'kanji' in x && 'meaning' in x,
    )
  }

  private audioKey(card: Card): string {
    return this.extraString(card, 'audio')
  }

  /**
   * ข้อความที่ให้เสียงระบบอ่าน
   *
   * คำศัพท์ใช้คำอ่านที่เป็น kana ไม่ใช่รูปที่มีคันจิ — คันจิตัวเดียวกันอ่านได้หลายเสียง
   * และ TTS เดาผิดได้ (今日 คือ きょう ไม่ใช่ こんにち) ส่วน kana อ่านออกมาตรงเสมอ
   */
  private spokenText(card: Card): string {
    return this.extraString(card, 'reading') || card.front
  }

  /**
   * มีเสียงให้เล่นไหม — ไฟล์ที่อัดไว้ หรือเสียงญี่ปุ่นของระบบ
   *
   * ต้องมีคำอ่านที่แน่นอนถึงจะอ่านออกเสียงได้ · คันจิเดี่ยวกับ radical ไม่มี
   * — 好 อ่าน コウ ใน 友好 แต่ す ใน 好き · เล่นเสียงเดียวไปเลยคือสอนผิด
   */
  private canSpeak(card: Card): boolean {
    if (this.audioKey(card)) return true
    return !!this.extraString(card, 'reading') && hasJapaneseVoice()
  }

  private async playCurrent(card: Card): Promise<void> {
    const key = this.audioKey(card)
    if (key) {
      await playKana(key, this.spokenText(card))
      return
    }
    speakText(this.spokenText(card))
  }

  // ---------- คีย์ลัด ----------

  private bindKeys(): void {
    this.els.audioBtn.addEventListener('click', () => {
      if (this.current) void this.playCurrent(this.current)
    })

    this.els.strokeBtn.addEventListener('click', () => this.toggleStroke())

    this.els.input.addEventListener('input', () => this.checkTyped())

    onPointerTypeChange(() => this.applyInputMode())

    // คลิกช่องคัดอักษร = ดูเฉลย · ใช้ได้ทั้งเมาส์และนิ้ว ชัดกว่า hover
    this.els.square.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.square-icons')) return
      this.showPeek()
    })
    this.els.square.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (this.answered) return
        this.showPeek()
        e.preventDefault()
      }
    })

    document.addEventListener('keydown', (e) => {
      const target = e.target as HTMLElement | null
      const typingInField = target?.tagName === 'INPUT' && target !== this.els.input

      if (e.key === 'Escape') {
        this.els.input.blur()
        return
      }

      if (e.key === '/' || e.key === '?') {
        if (typingInField) return
        this.showPeek()
        e.preventDefault()
        return
      }

      if (e.key === 'm' || e.key === 'M') {
        if (typingInField) return
        if (isTypingMode(this.mode) && !this.answered) return // m เป็นตัวอักษรใน romaji
        // เฉลยยังไม่เปิด = ยังไม่เห็นความหมาย ยังแต่งเรื่องไม่ได้
        if (this.current && !this.els.reveal.hidden) {
          this.openMnemonic(this.current)
          e.preventDefault()
        }
        return
      }

      if (e.key === 'w' || e.key === 'W') {
        if (typingInField) return
        if (isTypingMode(this.mode) && !this.answered) return // w เป็นตัวอักษรใน romaji
        this.toggleStroke()
        e.preventDefault()
        return
      }

      if (e.key === 'f' || e.key === 'F') {
        if (typingInField) return
        if (isTypingMode(this.mode) && !this.answered) return // f เป็นตัวอักษรใน romaji
        if (this.current) {
          void this.playCurrent(this.current)
          e.preventDefault()
        }
        return
      }

      // โหมดเรียน: Space = เข้าใจแล้ว ไม่ใช่ข้ามใบ
      // ถ้าปล่อยให้ตกไป next() ข้างล่าง ใบนั้นจะหลุดไปโดยไม่ถูกบันทึกว่าเรียนแล้ว
      if (isLessonMode(this.mode)) {
        if (e.key === ' ' || e.key === 'Enter') {
          if (this.current) void this.finishLesson(this.current)
          e.preventDefault()
        }
        return
      }

      // โหมดนึกเอง: Space เปิดเฉลย แล้ว 1-4 ให้เกรด
      if (this.mode === 'recall') {
        if (!this.answered && (e.key === ' ' || e.key === 'Enter')) {
          this.openGrading()
          e.preventDefault()
          return
        }
        if (this.answered && e.key >= '1' && e.key <= '4') {
          const btn = this.els.reveal.querySelector<HTMLButtonElement>(`.grade[data-r="${e.key}"]`)
          btn?.click()
          e.preventDefault()
        }
        return
      }

      if (this.answered && (e.key === ' ' || e.key === 'Enter')) {
        this.next()
        e.preventDefault()
        return
      }

      if (!isTypingMode(this.mode) && e.key >= '1' && e.key <= '4') {
        const tile = this.els.tiles.querySelectorAll<HTMLButtonElement>('.tile')[
          Number(e.key) - 1
        ]
        if (tile && !tile.disabled) tile.click()
        e.preventDefault()
      }
    })
  }

  // ---------- จุดอ่อน ----------

  /** ตัวที่ยังไม่แน่นในชุดที่ให้มา เรียงจากด่วนสุด */
  private weakOf(cards: Card[], forMode = this.mode): Weakness[] {
    /*
     * สถิติของโหมดที่กำลังจะถามมาก่อนเสมอ ถ้ามีมากพอจะตัดสิน
     *
     * ใบที่ตอบ "ตัวอักษร → ความหมาย" ได้ทุกครั้งอาจนึกรูปไม่ออกเลยในทางกลับ
     * ตัดสินด้วยคะแนนก้อนเดียวจะบอกว่าแน่นแล้ว ทั้งที่แน่นแค่ทางเดียว
     */
    const mine = [...this.modeStats].filter(([k]) => k.endsWith(`|${forMode}`))
    const refMode = medianModeMs(mine.map(([, v]) => v))

    const fallback = (c: Card): Weakness => {
      if (this.deck.scheduling === 'drill') {
        return drillWeakness(c, this.drill.get(c.id), medianMs([...this.drill.values()]))
      }
      return srsWeakness(c, this.progress.get(c.id), medianProgressMs([...this.progress.values()]))
    }

    return rankWeak(
      cards.map(
        (c) => modeWeakness(c, this.modeStats.get(`${c.id}|${forMode}`), refMode) ?? fallback(c),
      ),
    )
  }

  /** คะแนนความอ่อนของแต่ละใบ — ใช้จัดลำดับคิว ไม่ใช่แค่กรอง */
  private weakScores(cards: Card[]): Map<CardId, Weakness> {
    return new Map(this.weakOf(cards).map((w) => [w.cardId, w]))
  }

  /** โหมดเก็บตกกำลังกรอง pool อยู่จริงไหม */
  isWeakActive(): boolean {
    return this.weakActive
  }

  /** ยังไม่เคยเรียนกี่ใบในชุดที่เลือกไว้ */
  unseenInSelection(): number {
    return filterCards(this.deck, getPicked(this.deck.id)).filter((c) => this.isUnseen(c)).length
  }

  /** ใช้โชว์จำนวนบนปุ่มเก็บตก — คิดจากชุดที่เลือกไว้ ไม่ใช่ pool ที่อาจถูกกรองแล้ว */
  weakInSelection(): Weakness[] {
    return this.weakOf(filterCards(this.deck, getPicked(this.deck.id)))
  }

  /** สถานะของทุกใบใน deck — ใช้ระบายสีตารางเลือกการ์ด */
  weaknessMap(): Map<CardId, Weakness> {
    const all =
      this.deck.scheduling === 'drill'
        ? (() => {
            const ref = medianMs([...this.drill.values()])
            return this.deck.cards.map((c) => drillWeakness(c, this.drill.get(c.id), ref))
          })()
        : this.deck.cards.map((c) => srsWeakness(c, this.progress.get(c.id)))
    return new Map(all.map((w) => [w.cardId, w]))
  }

  /** สรุปความคืบหน้าโชว์บนหัว */
  summary(): { done: number; total: number; label: string } {
    const total = this.pool.length

    /*
     * โหมดเรียนไม่มีอะไรให้วัดว่า "จำได้" — นับว่าเรียนไปกี่ใบในรอบนี้แทน
     * stats.total ไม่ขยับในโหมดเรียนโดยตั้งใจ จึงนับจาก pool ที่ร่อยหรอลง
     *
     * ยกเว้นโหมดอัตโนมัติ ที่ this.mode เป็น lesson แค่ชั่วคราวตอนเจอใบใหม่ —
     * ตัวเลขบนหัวต้องพูดถึงคิวทั้งรอบเหมือนเดิม ไม่ใช่กระโดดไปเป็นโควตาใบใหม่
     */
    if (isLessonMode(this.mode) && !this.picker) {
      return {
        done: this.lessonBatch - total,
        total: this.lessonBatch,
        label: 'เรียนแล้ว',
      }
    }

    let done = 0
    if (this.deck.scheduling === 'drill') {
      for (const c of this.pool) if (drillMastered(this.drill.get(c.id))) done++
    } else {
      for (const c of this.pool) {
        const p = this.progress.get(c.id)
        if (p && p.state === CardState.Review && p.scheduledDays >= 5) done++
      }
    }
    return { done, total, label: 'จำได้แล้ว' }
  }

  sessionStats(): typeof this.stats {
    return this.stats
  }

  /**
   * เปลี่ยนโหมดต้องสร้าง pool ใหม่ ไม่ใช่แค่ขึ้นข้อถัดไป
   * โหมดเรียนกับโหมดฝึกใช้คนละชุด — เรียนเอาใบที่ยังไม่เคยเห็น ฝึกเอาใบที่เรียนแล้ว
   */
  async setMode(mode: ModeId): Promise<void> {
    settings.mode[this.deck.id] = mode
    saveSettings()
    await this.start(this.deck, mode)
  }

  modeLabel(): string {
    return MODE_LABEL[this.mode]
  }

  async reload(): Promise<void> {
    await this.start(this.deck, this.mode)
  }
}

/** ช่วงเวลาถัดไปแบบอ่านง่าย — โชว์บนปุ่มเกรดให้เห็นว่าเลือกแล้วจะเจออีกทีเมื่อไร */
function formatInterval(ms: number): string {
  const min = ms / 60_000
  if (min < 60) return `${Math.max(1, Math.round(min))} น.`
  const hr = min / 60
  if (hr < 24) return `${Math.round(hr)} ชม.`
  const day = hr / 24
  if (day < 31) return `${Math.round(day)} วัน`
  const mo = day / 30.4
  if (mo < 12) return `${Math.round(mo)} เดือน`
  return `${(mo / 12).toFixed(1)} ปี`
}

function shuffle<T>(a: T[]): void {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const tmp = a[i] as T
    a[i] = a[j] as T
    a[j] = tmp
  }
}
